import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useToast } from "../hooks/use-toast";
import {
  ArrowRight,
  Check,
  Users,
  DollarSign,
  Briefcase,
  Sparkles,
  HandCoins,
  GraduationCap,
  PenTool,
  LifeBuoy,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tiers = [
  {
    name: "Affiliate",
    commission: "20%",
    sub: "recurring",
    desc: "Refer customers. Earn on every renewal for as long as they stay.",
    requirements: "No minimum volume. Apply in 2 minutes.",
    color: "border-slate-200",
  },
  {
    name: "Partner",
    commission: "30%",
    sub: "recurring",
    desc: "For agencies actively reselling. Includes co-marketing & onboarding support.",
    requirements: "5+ active client accounts.",
    color: "border-[#d4af37] shadow-[0_20px_50px_-20px_rgba(212,175,55,0.4)]",
    featured: true,
  },
  {
    name: "Authority",
    commission: "40%",
    sub: "+ white-label",
    desc: "White-label the platform under your brand. Dedicated revenue strategist.",
    requirements: "By invitation or application review.",
    color: "border-slate-200",
  },
];

const benefits = [
  {
    icon: HandCoins,
    title: "Industry-leading commissions",
    desc: "Up to 40% recurring revenue share — paid monthly, no caps, no clawbacks after month 3.",
  },
  {
    icon: PenTool,
    title: "Co-branded marketing assets",
    desc: "Pre-built landing pages, decks, email sequences, and case-study templates ready to deploy.",
  },
  {
    icon: GraduationCap,
    title: "Affiliate Academy",
    desc: "On-demand training on the platform, ICP discovery, and discovery-call playbooks that close.",
  },
  {
    icon: LifeBuoy,
    title: "Dedicated partner manager",
    desc: "A real human on Slack or email within 4 business hours. No support tickets, no auto-replies.",
  },
  {
    icon: Briefcase,
    title: "Deal registration",
    desc: "Register opportunities up front. We protect your commission even if the prospect calls us first.",
  },
  {
    icon: Users,
    title: "Joint-client roadmap",
    desc: "Your clients get priority routing for feature requests. Your roadmap influences ours.",
  },
];

const steps = [
  {
    n: "01",
    title: "Apply",
    desc: "Tell us about your agency, your book of business, and your target verticals. Two minutes, online form.",
  },
  {
    n: "02",
    title: "Onboard",
    desc: "Meet your partner manager. Get your portal access, asset library, and a custom referral link.",
  },
  {
    n: "03",
    title: "Earn",
    desc: "Refer or resell. Commissions are tracked in real time and paid monthly via ACH or PayPal.",
  },
];

export default function AffiliatePage() {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    website: "",
    monthlyClients: "1-5",
    verticals: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast({ title: "Missing fields", description: "Name, email, and company are required." });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/affiliates`, {
        name: form.name,
        email: form.email,
        company: form.company,
        website: form.website,
        monthly_clients: form.monthlyClients,
        verticals: form.verticals,
      });
      const apptUrl = res?.data?.appointmentUrl;
      toast({
        title: apptUrl ? "Application received — opening calendar…" : "Application received",
        description: apptUrl ? "Pick a partner-intro slot below." : "We'll review and reach out within 2 business days.",
      });
      setForm({ name: "", email: "", company: "", website: "", monthlyClients: "1-5", verticals: "" });
      if (apptUrl) {
        const sep = apptUrl.includes("?") ? "&" : "?";
        const target = `${apptUrl}${sep}name=${encodeURIComponent(form.name)}&email=${encodeURIComponent(form.email)}`;
        setTimeout(() => { window.location.href = target; }, 600);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast({ title: "Could not submit", description: typeof msg === "string" ? msg : "Validation failed." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Affiliate Program — BuyerBeacon Pro"
        description="Earn recurring commissions promoting BuyerBeacon Pro. Generous splits, 60-day cookies, and dedicated partner support. Apply in minutes."
        path="/affiliate"
      />
      <Navbar />
      <main className="pt-28">
        {/* Hero */}
        <section className="relative overflow-hidden pb-12">
          <div className="pointer-events-none absolute left-1/2 top-[60px] -z-10 h-[340px] w-[680px] -translate-x-1/2 rounded-full bg-[#d4af37]/15 blur-[120px]" />
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                <Sparkles className="h-3 w-3 text-[#d4af37]" />
                The BuyerBeacon Affiliate Program
              </span>
              <h1 className="mx-auto mt-4 max-w-4xl text-5xl font-medium tracking-tight text-[#1e1b4b] sm:text-6xl">
                Earn 20–40% recurring
                <br />
                <span className="text-slate-400">on every account you bring.</span>
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-[16px] leading-relaxed text-slate-600">
                Built for agencies, consultants, and resellers who already serve
                marketing teams. Industry-leading commissions, real partner
                support, and full white-label at the top tier.
              </p>

              <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <a
                  href="#apply"
                  className="group inline-flex items-center gap-2 rounded-xl bg-[#1e1b4b] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_12px_30px_-10px_rgba(30,27,75,0.55)] transition-all hover:bg-[#1e1b4b]/92"
                >
                  Apply Now
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </a>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3.5 text-sm font-medium text-[#1e1b4b] hover:border-[#1e1b4b]/30 hover:bg-slate-50"
                >
                  See Platform Pricing
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Commission tiers */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Commission Structure
              </p>
              <h2 className="mt-3 text-4xl font-medium tracking-tight text-[#1e1b4b]">
                Three tiers. <span className="text-slate-400">One transparent ladder.</span>
              </h2>
            </div>

            <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
              {tiers.map((t) => (
                <div
                  key={t.name}
                  className={`relative flex flex-col rounded-2xl border-2 bg-white p-7 transition-all hover:-translate-y-1 ${t.color}`}
                >
                  {t.featured && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#d4af37] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#1e1b4b]">
                      Most Common
                    </span>
                  )}
                  <h3 className="text-lg font-semibold text-[#1e1b4b]">{t.name}</h3>
                  <div className="mt-5 flex items-baseline gap-2">
                    <span className="bg-gradient-to-br from-[#1e1b4b] to-[#d4af37] bg-clip-text text-6xl font-medium tracking-tight text-transparent">
                      {t.commission}
                    </span>
                    <span className="text-sm text-slate-500">{t.sub}</span>
                  </div>
                  <p className="mt-5 text-sm leading-relaxed text-slate-600">{t.desc}</p>
                  <div className="mt-6 flex items-start gap-2 border-t border-slate-100 pt-5 text-xs text-slate-500">
                    <Check className="mt-0.5 h-3.5 w-3.5 text-[#7c5e0a]" />
                    {t.requirements}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits grid */}
        <section className="border-t border-slate-200/80 bg-slate-50/40 py-20">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                What You Get
              </p>
              <h2 className="mt-3 text-4xl font-medium tracking-tight text-[#1e1b4b]">
                Built for partners who <span className="text-slate-400">actually ship.</span>
              </h2>
            </div>

            <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
              {benefits.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.title} className="flex flex-col bg-white p-6 transition-colors hover:bg-slate-50/70">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1e1b4b] text-[#d4af37]">
                      <Icon className="h-4 w-4" />
                    </div>
                    <h3 className="mt-5 text-base font-semibold text-[#1e1b4b]">{b.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-20">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                How It Works
              </p>
              <h2 className="mt-3 text-4xl font-medium tracking-tight text-[#1e1b4b]">
                Apply. Onboard. <span className="text-slate-400">Earn.</span>
              </h2>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
              {steps.map((s) => (
                <div key={s.n} className="rounded-2xl border border-slate-200 bg-white p-7">
                  <span className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#d4af37]">
                    {s.n}
                  </span>
                  <h3 className="mt-4 text-2xl font-semibold text-[#1e1b4b]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application form */}
        <section id="apply" className="border-t border-slate-200/80 bg-[#faf8f3] py-20">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-5 lg:grid-cols-2 lg:px-8">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Application
              </p>
              <h2 className="mt-3 text-4xl font-medium tracking-tight text-[#1e1b4b]">
                Apply in 2 minutes.
              </h2>
              <p className="mt-4 max-w-md text-[15px] leading-relaxed text-slate-600">
                Tell us a little about your business. We'll review every
                application personally and respond within two business days.
              </p>

              <ul className="mt-8 space-y-3 text-sm">
                {[
                  "No exclusivity required",
                  "Commission tracked in real time",
                  "Paid monthly via ACH or PayPal",
                  "Cancel anytime — keep what you've earned",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-[#1e1b4b]">
                    <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#d4af37]/15">
                      <Check className="h-3 w-3 text-[#7c5e0a]" />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mt-10 flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm">
                <DollarSign className="h-5 w-5 text-[#d4af37]" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Average partner earnings</p>
                  <p className="font-semibold text-[#1e1b4b]">$2,800 / mo after 90 days</p>
                </div>
              </div>
            </div>

            <form
              onSubmit={submit}
              className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_20px_60px_-30px_rgba(30,27,75,0.2)]"
            >
              <h3 className="text-lg font-semibold text-[#1e1b4b]">Partner application</h3>
              <p className="mt-1 text-sm text-slate-500">All fields below are confidential.</p>

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Your name" value={form.name} onChange={(v) => update("name", v)} />
                <Field label="Work email" type="email" value={form.email} onChange={(v) => update("email", v)} />
                <Field label="Company / agency" value={form.company} onChange={(v) => update("company", v)} />
                <Field label="Website" value={form.website} onChange={(v) => update("website", v)} />
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-slate-600">Active clients</label>
                  <select
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#1e1b4b] outline-none focus:border-[#1e1b4b]/40 focus:ring-2 focus:ring-[#d4af37]/20"
                    value={form.monthlyClients}
                    onChange={(e) => update("monthlyClients", e.target.value)}
                  >
                    {["1-5", "6-15", "16-50", "50+"].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <Field label="Verticals served" value={form.verticals} onChange={(v) => update("verticals", v)} />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#1e1b4b] px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1e1b4b]/92 disabled:opacity-60"
              >
                {submitting ? "Submitting…" : "Submit Application"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <p className="mt-3 text-center text-[11px] text-slate-500">
                By applying, you agree to our Partner Terms.
              </p>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#1e1b4b] outline-none transition-colors focus:border-[#1e1b4b]/40 focus:ring-2 focus:ring-[#d4af37]/20"
      />
    </div>
  );
}
